import type { ReactNode } from 'react';
import { ShieldSlashIcon } from '@phosphor-icons/react';
import { fmtMoneyWhole } from '../results/flight_format.js';
import { carrierNameOf } from '../results/flight_offer_source.js';
import type { FlightAirportSwitchView } from '../results/offer/offer_copy.js';
import { useOfferLayout } from '../results/offer/use_offer_layout.js';
import { useAirportSwitchGate } from './airport_switch_gate.js';
import { FareExpansion, type TariffFetchState } from './fare_expansion.js';
import {
  asymmetricChecked,
  comboFares,
  summarizeSelection,
  type AsymmetricChecked,
  type FareSel,
  type SelectionSummary,
} from './fare_selection.js';
import type { FlightFareSource } from './fare_source.js';
import type { FaresText, FlightFaresCopy } from './fares_copy.js';
import { FaresPageStack } from './fares_page_stack.js';
import {
  FaresStatus,
  type FaresLegKind,
  type FaresPageFlight,
  type OpenFlightDetail,
} from './fares_page_shared.js';
import { SelectedFlightRow } from './selected_flight_row.js';
import './fares_page.css';

/**
 * **"Escolha as tarifas de cada voo" — a tela de tarifa do determinístico
 * (#2262, padronizada em 30/09 pela #3875).**
 *
 * A escolha de tarifa SAIU da lista de voos e virou página. O gatilho é um
 * relato de avaliação ("I was looking for a comparative between Economy, Economy
 * business and Business"): embaixo de uma linha da lista, as famílias empilham e
 * só se comparam de cima para baixo, uma perna de cada vez. Aqui elas ficam lado
 * a lado, cada uma com a lista INTEIRA de benefícios, e as duas pernas na mesma
 * tela — a comparação passa a ser horizontal, que é como a pergunta é feita.
 *
 * A tela é DERIVADA, sem estado de domínio próprio: o voo de cada perna já foi
 * escolhido (o explorer os guarda em `chosen`), a seleção de tarifa vive no
 * `useOfferFares` do host, e o único gesto que sai daqui é o mesmo `fares_picked`
 * que a escada já emitia. "Alterar voo" devolve a perna à lista (`pick_removed`),
 * e é ele que faz a página sair de cena.
 *
 * **Um corpo só com a conversa.** O desenho largo é UM card: os voos escolhidos
 * em cima e, embaixo, o MESMO `FareExpansion` que o card do chat expande no
 * desktop — trilho por perna nomeado pela companhia, aviso de bagagem
 * assimétrica, Total, combinação e o CTA do host no rodapé. A saia flutuante
 * que resumia as pernas morreu: no app ela cobria a última linha dos cards e a
 * legenda das regras. O desenho do celular (`FaresPageStack`) é a outra árvore,
 * medida pelo container.
 *
 * **Todos os modos.** Só ida e multitrecho chegam aqui depois do pick do voo,
 * como o ida e volta já fazia; com uma perna só a tela mostra um voo e um
 * trilho. Ver `isFaresStage`.
 *
 * **Troca de cabine não entra** — depende do endpoint Get-Tariff por voo,
 * guardado no spike #2371. As famílias desta tela são as da cabine BUSCADA.
 */

export interface FlightFaresPageProps {
  /** O cabeçalho da página, que o host escreve do pedido corrente: rota, forma
   *  e (quando sabe) quantas pessoas viajam. */
  subtitle: string;
  /** A cabine buscada, já no idioma — o cartão do voo no celular a escreve. */
  cabin?: string;
  outbound: FaresPageFlight;
  /** A volta. Ausente = uma perna só (one-way do chat e do explorer, trecho do
   *  roteiro): a tela do celular (#3875) é a mesma para as duas formas; o
   *  desenho largo continua sendo montado só pelo ida e volta do explorer. */
  inbound?: FaresPageFlight;
  /** Fetch do tariff do PAR (o `offerId` da fase 2 carrega as duas pernas). */
  state: TariffFetchState;
  sel: FareSel;
  /** Congelado (checkout em cena): sem radio, sem "Alterar voo", sem CTA. */
  readOnly?: boolean;
  /** A volta parte de outro aeroporto que o da chegada da ida (#4203), já
   *  decidido pelo host, que conhece o pedido: a tela pede ciência antes do CTA. */
  airportSwitch?: FlightAirportSwitchView | null;
  /** O drawer de detalhe e assentos é do host — ver `OpenFlightDetail`. */
  onOpenDetail: OpenFlightDetail;
  text: FaresText;
  /** Teto da política do CONJUNTO da fase 2 — a linha da direita do "Voos
   *  selecionados", mesma convenção da lista (#2352). */
  ceiling?: { amount: number; currency: string };
  onPick: (patch: Partial<FareSel>) => void;
  /** Devolve a perna à lista. `0` = ida (derruba a volta junto, porque os pares
   *  da fase 2 saíram do ref dela); `1` = volta. */
  onChangeFlight: (leg: 0 | 1) => void;
  onCheckout: (picked: { total: number; currency: string; names: string }) => void;
  onRetry: () => void;
  /** Recuperação do `stale` (handle morto, #2332) — re-buscar é o único caminho;
   *  o retry repetiria o mesmo 409 para sempre. */
  onResearch?: () => void;
  /** A seta de voltar do cabeçalho do celular. Só a casca de tela cheia do
   *  chat (#3875) a passa; no explorer o chrome dele já tem a saída. */
  onBack?: () => void;
  /** O verbo do CTA. Ausente ⇒ "Ir para checkout" (a bancada, onde o pick
   *  fecha a viagem). O "Ver todos" da conversa passa "Confirmar" (o pick volta
   *  à conversa) e o card do chat "Próximo" (o pick vai ao agente) — o mesmo
   *  contrato do rodapé da expansão inline (`FareExpansion.cta`). */
  ctaLabel?: string;
  /** Trava o CTA por razão do HOST (o draftLock do explorer). A seleção
   *  incompleta já trava sozinha. */
  ctaDisabled?: boolean;
  /** O host está processando o pick (a bancada cotando): o CTA fica em cena
   *  girando, mesmo `readOnly` — ver `FareExpansionCta.busy`. */
  ctaBusy?: boolean;
}

/** Cabeçalho da página + a linha de "Voos selecionados" com o teto de política. */
function FaresPageHead({
  subtitle,
  ceiling,
  copy,
}: Readonly<{
  subtitle: string;
  ceiling: FlightFaresPageProps['ceiling'];
  copy: FlightFaresCopy;
}>) {
  return (
    <header className="ffs-head-page">
      {/* h1: a tela de tarifas é uma tela em diálogo — é a página que a pessoa está
          lendo, e sem um h1 o leitor de tela não tem por onde começar (#3239 C6).
          `tabIndex={-1}`: é onde o foco pousa ao voltar do checkout
          (`useResumeSeq`) — fora da ordem de Tab, focável por script. */}
      <h1 className="ffs-title" tabIndex={-1}>
        {copy.title}
      </h1>
      <p className="ffs-subtitle">{subtitle}</p>
      <div className="ffs-section">
        <h3 className="ffs-section-title">{copy.selectedFlights}</h3>
        {ceiling && (
          // `role="note"`, como na lista: aviso passivo sobre o conjunto, não
          // uma região viva que interrompe a leitura.
          <p className="ffs-policy" role="note">
            <ShieldSlashIcon size={14} aria-hidden />
            <span>
              {copy.policyCeilingAbove(fmtMoneyWhole(ceiling))}{' '}
              <span className="ffs-policy-out">{copy.policyCeilingOut}</span>
            </span>
          </p>
        )}
      </div>
    </header>
  );
}

/** O que os dois desenhos recebem já derivado — ver `FaresPageStack`. */
export interface FaresDrawingProps {
  subtitle: string;
  cabin: string | undefined;
  /** `[ida]` ou `[ida, volta]`. */
  flights: FaresPageFlight[];
  state: TariffFetchState;
  sel: FareSel;
  readOnly: boolean | undefined;
  ceiling: { amount: number; currency: string } | undefined;
  bundled: boolean;
  combos: readonly FlightFareSource[];
  summary: SelectionSummary | null;
  asymmetric: AsymmetricChecked | null;
  onOpenDetail: OpenFlightDetail;
  onPick: (patch: Partial<FareSel>) => void;
  onChangeFlight: (leg: 0 | 1) => void;
  onCheckout: (picked: { total: number; currency: string; names: string }) => void;
  onRetry: () => void;
  onResearch: (() => void) | undefined;
  onBack: (() => void) | undefined;
  ctaLabel: string;
  ctaDisabled: boolean;
  ctaBusy: boolean;
  /** O portão da volta de outro aeroporto (#4203), logo abaixo do cabeçalho. */
  notice: ReactNode;
  text: FaresText;
}

export function FlightFaresPage({
  outbound,
  inbound,
  state,
  sel,
  readOnly,
  ceiling,
  onResearch,
  onBack,
  ctaLabel,
  ctaDisabled = false,
  ctaBusy = false,
  airportSwitch,
  cabin,
  text,
  ...rest
}: Readonly<FlightFaresPageProps>) {
  // O desenho do celular (#3875) é outra árvore, medida pelo CONTAINER (a
  // mesma régua e o mesmo motivo do card de oferta — ver `use_offer_layout`).
  const { ref, layout } = useOfferLayout<HTMLDivElement>();
  const view = state.phase === 'ready' ? state.view : null;
  const combos = view ? comboFares(view) : [];
  // Bundled = a companhia tarifa o PAR junto: uma família só, que cobre as duas
  // pernas. O frame desenha uma família por perna porque é o caso normal do
  // provider (`bundled: []`), mas oferecer duas colunas independentes sobre
  // combos deixaria montar combinação que não existe. Então o par mantém os DOIS
  // voos escolhidos em cena e o trilho vira um só, embaixo dos dois.
  const bundled = combos.length > 0;
  const summary = view ? summarizeSelection(view, combos, sel) : null;
  const gate = useAirportSwitchGate(airportSwitch, outbound, inbound, readOnly, text.copy);
  const drawing: FaresDrawingProps = {
    ...rest,
    cabin,
    text,
    flights: inbound ? [outbound, inbound] : [outbound],
    state,
    sel,
    readOnly,
    ceiling,
    bundled,
    combos,
    summary,
    asymmetric: summary ? asymmetricChecked(summary.legs, text.classify) : null,
    onResearch,
    onBack,
    ctaLabel: ctaLabel ?? text.copy.checkout,
    ctaDisabled: ctaDisabled || gate.locked,
    ctaBusy,
    notice: gate.notice,
  };
  return (
    <div ref={ref} className="ffs">
      {layout === 'stack' ? <FaresPageStack {...drawing} /> : <FaresPageWide {...drawing} />}
    </div>
  );
}

/** O desenho LARGO — os voos escolhidos e o corpo da expansão do chat num
 *  card só (`.ffs-leg`). O `FareExpansion` só monta com o tariff `ready`: nos
 *  outros estados a página mantém o contexto de pé e troca só o miolo pela
 *  faixa de espera/falha (`FaresStatus`), como sempre fez. */
function FaresPageWide({
  subtitle,
  flights,
  state,
  sel,
  readOnly,
  ceiling,
  onOpenDetail,
  onPick,
  onChangeFlight,
  onCheckout,
  onRetry,
  onResearch,
  ctaLabel,
  ctaDisabled,
  ctaBusy,
  notice,
  text,
}: Readonly<FaresDrawingProps>) {
  const [outbound, inbound] = flights;
  if (!outbound) return null;
  const rowOf = (flight: FaresPageFlight, kind: FaresLegKind, leg: 0 | 1) => (
    <SelectedFlightRow
      kind={kind}
      seg={flight.seg}
      stops={flight.stops}
      {...(readOnly ? { readOnly } : {})}
      copy={text.copy}
      locale={text.locale}
      onDetail={() => {
        onOpenDetail(flight, kind);
      }}
      // Trecho DIRETO só: o mapa é pedido por identidade de perna, e uma conexão
      // não tem uma só (mesma regra da linha de ofertas).
      {...(flight.stops <= 0
        ? {
            onSeats: () => {
              onOpenDetail(flight, kind, 'seats');
            },
          }
        : {})}
      {...(readOnly
        ? {}
        : {
            onChangeFlight: () => {
              onChangeFlight(leg);
            },
          })}
    />
  );

  return (
    <>
      <FaresPageHead subtitle={subtitle} ceiling={ceiling} copy={text.copy} />
      {notice}
      <div className="ffs-legs">
        <section className="ffs-leg">
          {rowOf(outbound, 'outbound', 0)}
          {inbound && rowOf(inbound, 'return', 1)}
          {state.phase === 'ready' ? (
            <FareExpansion
              state={state}
              sel={sel}
              {...(readOnly ? { readOnly } : {})}
              // A companhia de MARKETING de cada perna nomeia o trilho — a mesma
              // régua do card do chat (`rowActions`).
              legCarriers={[
                carrierNameOf(outbound.seg) || undefined,
                inbound ? carrierNameOf(inbound.seg) || undefined : undefined,
              ]}
              cta={{ mode: 'confirm', label: ctaLabel, disabled: ctaDisabled, busy: ctaBusy }}
              onPick={onPick}
              onSelect={onCheckout}
              onRetry={onRetry}
              {...(onResearch ? { onResearch } : {})}
              text={text}
            />
          ) : (
            <FaresStatus
              state={state}
              onRetry={onRetry}
              onResearch={onResearch}
              onChangeFlight={onChangeFlight}
              readOnly={readOnly}
              copy={text.copy}
            />
          )}
        </section>
      </div>
    </>
  );
}
